import React, { useState, useEffect } from 'react';
import './AddPersonModal.css';

function AddPersonModal({ isOpen, onClose, groups = [], onAddPerson, onCreateGroup, initialName = '', preselectedGroupId = null, skipGroupSelection = false }) {
  const [step, setStep] = useState(1); // 1 = name, 2 = group selection
  const [name, setName] = useState('');
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Set initial name when modal opens with a pre-filled name
  useEffect(() => {
    if (isOpen && initialName) {
      setName(initialName);
    }
  }, [isOpen, initialName]);

  // New group creation
  const [showNewGroupInput, setShowNewGroupInput] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [isCreatingGroup, setIsCreatingGroup] = useState(false);

  const handleNameSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    setError('');

    // If skipping group selection (first person flow) or group is preselected, add directly
    if (skipGroupSelection || preselectedGroupId) {
      setIsSubmitting(true);
      try {
        await onAddPerson({
          name: name.trim(),
          groupId: preselectedGroupId || null
        });
        resetForm();
        onClose();
      } catch (err) {
        setError(err.message || 'Failed to add person');
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    setStep(2);
  };

  const handleGroupSelect = async (groupId) => {
    setSelectedGroupId(groupId);
    setIsSubmitting(true);
    setError('');

    try {
      await onAddPerson({
        name: name.trim(),
        groupId: groupId
      });
      // Reset form
      resetForm();
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to add person');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateGroup = async (e) => {
    e.preventDefault();
    if (!newGroupName.trim() || !onCreateGroup) return;

    setIsCreatingGroup(true);
    setError('');

    try {
      const newGroup = await onCreateGroup(newGroupName.trim());
      if (newGroup) {
        // Immediately add the person to the new group
        await onAddPerson({
          name: name.trim(),
          groupId: newGroup.id
        });
        resetForm();
        onClose();
      }
    } catch (err) {
      setError(err.message || 'Failed to create group');
    } finally {
      setIsCreatingGroup(false);
    }
  };

  const resetForm = () => {
    setName('');
    setSelectedGroupId('');
    setError('');
    setStep(1);
    setShowNewGroupInput(false);
    setNewGroupName('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleBack = () => {
    if (showNewGroupInput) {
      setShowNewGroupInput(false);
      setNewGroupName('');
      setError('');
    } else {
      setError('');
      setStep(1);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="add-person-overlay" onClick={handleClose}>
      <div className="add-person-modal" onClick={(e) => e.stopPropagation()}>
        {step === 1 ? (
          <div className="add-person-step" key="step1">
            <h2>Add Person</h2>
            <form onSubmit={handleNameSubmit}>
              <div className="add-person-field">
                <label htmlFor="person-name">Name</label>
                <input
                  id="person-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter name..."
                  autoComplete="off"
                />
              </div>

              {error && <div className="add-person-error">{error}</div>}

              <div className="add-person-actions">
                <button
                  type="button"
                  className="add-person-cancel"
                  onClick={handleClose}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="add-person-submit"
                  disabled={!name.trim() || isSubmitting}
                >
                  {isSubmitting ? 'Adding...' : (skipGroupSelection || preselectedGroupId ? 'Add' : 'Next')}
                </button>
              </div>
            </form>
          </div>
        ) : showNewGroupInput ? (
          <div className="add-person-step" key="step-new-group">
            <h2>Create a new group for {name}</h2>
            <form onSubmit={handleCreateGroup}>
              <div className="add-person-field">
                <label htmlFor="new-group-name">Group Name</label>
                <input
                  id="new-group-name"
                  type="text"
                  value={newGroupName}
                  onChange={(e) => setNewGroupName(e.target.value)}
                  placeholder="Enter group name..."
                />
              </div>

              {error && <div className="add-person-error">{error}</div>}

              <div className="add-person-actions">
                <button
                  type="button"
                  className="add-person-cancel"
                  onClick={handleBack}
                  disabled={isCreatingGroup}
                >
                  Back
                </button>
                <button
                  type="submit"
                  className="add-person-submit"
                  disabled={!newGroupName.trim() || isCreatingGroup}
                >
                  {isCreatingGroup ? 'Creating...' : 'Create & Add'}
                </button>
              </div>
            </form>
          </div>
        ) : (
          <div className="add-person-step" key="step2">
            <h2>Which group should I put {name} in?</h2>

            <div className="add-person-groups">
              {groups.map((group) => (
                <button
                  key={group.id}
                  className={`add-person-group-btn ${selectedGroupId === group.id ? 'selected' : ''}`}
                  onClick={() => handleGroupSelect(group.id)}
                  disabled={isSubmitting}
                >
                  {group.color && (
                    <span
                      className="add-person-group-color"
                      style={{ backgroundColor: group.color }}
                    />
                  )}
                  {group.name}
                </button>
              ))}

              {/* Create new group option */}
              <button
                className="add-person-group-btn add-person-new-group-btn"
                onClick={() => setShowNewGroupInput(true)}
                disabled={isSubmitting}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                Create new group
              </button>
            </div>

            {error && <div className="add-person-error">{error}</div>}

            <div className="add-person-actions">
              <button
                type="button"
                className="add-person-cancel"
                onClick={handleBack}
                disabled={isSubmitting}
              >
                Back
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AddPersonModal;
